// AUTO-GENERATED from docs/TECHNICAL_PRD.md — CID Agent PWA API layer
// Module: My Sales (list, detail, draft create/submit, attachments, comments/notes, receipts, my-commissions)
// Every list (collection GET) takes params { page, limit, ...filters }.
import client from './client';

/**
 * GET /api/agent/sales [LIST · paginated]
 * Paginated list of the caller's own sales (My Sales tab) with search/status/deal_type/stage/date filters.
 * @disposition ADAPT_SCOPE · maps to GET / (controller.listSales → service.listSales)
 */
export const listSales = (params = {}) => client.get(`/api/agent/sales`, { params });

/**
 * GET /api/agent/sales/:id
 * Full detail of one of the caller's sales: parties, agents, co-agencies, attachments, notes, receipts, listing/project.
 * @disposition ADAPT_SCOPE · maps to GET /:id (controller.getSaleById → service.getSaleById)
 */
export const getSalesById = (id, params = {}) => client.get(`/api/agent/sales/${id}`, { params });

/**
 * GET /api/agent/sales/:id/edit
 * Raw editable sale (scalar fields + party ID arrays + agent splits) to prefill the edit form.
 */
export const getSalesEditById = (id, params = {}) => client.get(`/api/agent/sales/${id}/edit`, { params });

/**
 * POST /api/agent/sales
 * Agent creates a sale (self-authored deal), stamped created_by AGENT + their id and
 * landing in the same Pending pile an HQ-created sale does. There is no submit step:
 * creating it IS the handoff, and HQ converts from there.
 * @disposition BUILD_NEW · maps to POST / (controller.createSale → service.createSale)
 */
export const createSales = (data = {}) => client.post(`/api/agent/sales`, data);

/**
 * PUT /api/agent/sales/:id
 * Agent edits their own sale — everything while it is Pending; once invoiced, the
 * info fields and the figures. On an invoiced sale it REFUSES `agents` /
 * `co_agencies`: those go through previewParties / changeParties below.
 * @disposition ADAPT_SCOPE · maps to PUT /:id (controller.updateSale → service.updateSale)
 */
export const updateSalesById = (id, data = {}) => client.put(`/api/agent/sales/${id}`, data);

// HQ works the party change out (the commission engine, the GCI correction and
// the refusals all live in the admin server), so both calls wait on a second hop.
const PARTY_CHANGE_TIMEOUT = 45000;

/**
 * POST /api/agent/sales/:id/parties/preview — what changing an INVOICED sale's
 * co-agencies / referrals and agent splits would do. Writes nothing.
 * Body: { co_agencies?, agents? } — each the FULL new list, sent only when it
 * changed. co_agencies rows are the shape a Pending save sends; agents are
 * { agent_id, split_percentage } and must still include the caller.
 * Returns { what, summary, changes, hand_set, lines, money, cash, gci, points, blocked, can_save }.
 * A split that does not total 100%, or one without the caller, is a 400.
 */
export const previewParties = (id, data = {}) =>
  client.post(`/api/agent/sales/${id}/parties/preview`, data, { timeout: PARTY_CHANGE_TIMEOUT });

/**
 * POST /api/agent/sales/:id/parties/change — make that change. Same body plus a
 * required `reason`; a change the preview would block is a 400 with the reason why.
 */
export const changeParties = (id, data = {}) =>
  client.post(`/api/agent/sales/${id}/parties/change`, data, { timeout: PARTY_CHANGE_TIMEOUT });

/**
 * POST /api/agent/sales/:id/void — cancel the agent's own PENDING sale (before HQ
 * conversion). Releases the reserved project unit server-side, and the listing too
 * unless a booking form survives to keep holding it.
 *
 * `data` takes the same booking-form decision the admin void modal asks for:
 * { cancel_booking_form, cancellation_reason }. Omitted → the form is kept and
 * unlinked so it can be converted again.
 */
export const createSalesVoidById = (id, data = {}) => client.post(`/api/agent/sales/${id}/void`, data);

/**
 * PUT /api/agent/sales/:id/stage
 * Agent advances their Converted sale to a REQUEST-type stage (request invoice/receipt,
 * ready to claim, documents completed). Governance stages stay HQ-only.
 * @disposition BUILD_NEW · maps to PUT /:id/stage (service.updateStage)
 */
export const updateSalesStageById = (id, data = {}) => client.put(`/api/agent/sales/${id}/stage`, data);

/**
 * DELETE /api/agent/sales/:id
 * Agent discards their own un-submitted draft. The booking-form decision rides in
 * the request body (axios: `{ data: { cancel_booking_form, cancellation_reason } }`),
 * which the server also accepts as query params.
 * @disposition ADAPT_SCOPE · maps to DELETE /:id (controller.deleteSale → service.deleteSale)
 */
export const deleteSalesById = (id, config = {}) => client.delete(`/api/agent/sales/${id}`, config);

/**
 * POST /api/agent/sales/:id/attachments/upload-url
 * Get a signed Supabase Storage upload URL for a sale document (SPA/TA, claim docs, etc.).
 * @disposition ADAPT_SCOPE · maps to POST /:id/attachments/upload-url (controller.generateUploadUrl)
 */
export const createSalesAttachmentsUploadUrlById = (id, data = {}) => client.post(`/api/agent/sales/${id}/attachments/upload-url`, data);

/**
 * POST /api/agent/sales/:id/attachments/confirm-upload
 * Persist the uploaded attachment row and (for SPA_TA) enqueue extraction.
 * @disposition ADAPT_SCOPE · maps to POST /:id/attachments/confirm-upload (controller.confirmUpload → service.confirmUpload)
 */
export const createSalesAttachmentsConfirmUploadById = (id, data = {}) => client.post(`/api/agent/sales/${id}/attachments/confirm-upload`, data);

/**
 * GET /api/agent/sales/:id/attachments [LIST · paginated]
 * List sale documents with signed URLs, receipt-link flags, extraction badges.
 * @disposition ADAPT_SCOPE · maps to GET /:id/attachments (controller.listAttachments)
 */
export const listSalesAttachments = (id, params = {}) => client.get(`/api/agent/sales/${id}/attachments`, { params });

/**
 * DELETE /api/agent/sales/:id/attachments/:attachmentId
 * Agent removes an attachment they uploaded (before HQ verifies it).
 * @disposition ADAPT_SCOPE · maps to DELETE /:id/attachments/:attachmentId (service.deleteAttachment)
 */
export const deleteSalesAttachmentsByIdAttachmentId = (id, attachmentId, config = {}) => client.delete(`/api/agent/sales/${id}/attachments/${attachmentId}`, config);

/**
 * POST /api/agent/sales/:id/notes
 * Agent posts a comment / reply on their sale.
 * Body: { content, content_json, parent_note_id? } — content_json is the TipTap
 * document, which is what carries rich text, @mentions and /resource links.
 * @disposition ADAPT_SCOPE · maps to POST /:id/notes (controller.addNote → service.addNote)
 */
export const createSalesNotesById = (id, data = {}) => client.post(`/api/agent/sales/${id}/notes`, data);

/**
 * GET /api/agent/sales/:id/notes [LIST · paginated]
 * Threaded comments with reactions, replies, note attachments.
 * @disposition ADAPT_SCOPE · maps to GET /:id/notes (controller.listNotes → service.listNotes)
 */
export const listSalesNotes = (id, params = {}) => client.get(`/api/agent/sales/${id}/notes`, { params });

/**
 * PUT /api/agent/sales/:id/notes/:noteId
 * Agent edits their own comment. Previous versions are kept in sale_note_edit_history.
 * @disposition ADAPT_SCOPE · maps to PUT /:id/notes/:noteId (service.updateNote)
 */
export const updateSalesNotesByIdNoteId = (id, noteId, data = {}) => client.put(`/api/agent/sales/${id}/notes/${noteId}`, data);

/**
 * DELETE /api/agent/sales/:id/notes/:noteId
 * Agent soft-deletes their own comment — it stays in the thread as a placeholder.
 * @disposition ADAPT_SCOPE · maps to DELETE /:id/notes/:noteId (service.deleteNote)
 */
export const deleteSalesNotesByIdNoteId = (id, noteId, config = {}) => client.delete(`/api/agent/sales/${id}/notes/${noteId}`, config);

/**
 * GET /api/agent/sales/:id/notes/unread-count
 * How many human comments on this sale the caller hasn't read — the notes
 * button's badge. ERP/audit entries and the caller's own notes never count.
 */
export const getSalesNotesUnreadCount = (id) => client.get(`/api/agent/sales/${id}/notes/unread-count`);

/**
 * POST /api/agent/sales/:id/notes/read
 * Mark the thread read for the caller. Sent when the notes screen opens.
 */
export const createSalesNotesRead = (id) => client.post(`/api/agent/sales/${id}/notes/read`);

/**
 * GET /api/agent/sales/:id/notes/mentionable-users
 * Who the agent may @mention on this sale: active HQ staff + the sale's agents.
 */
export const listSalesNotesMentionableUsers = (id) => client.get(`/api/agent/sales/${id}/notes/mentionable-users`);

/**
 * GET /api/agent/sales/:id/notes/mentionable-resources
 * What `/` can link to: the sale's receipts and documents.
 */
export const listSalesNotesMentionableResources = (id) => client.get(`/api/agent/sales/${id}/notes/mentionable-resources`);

/**
 * POST /api/agent/sales/:id/notes/:noteId/reactions — react to any comment on the sale.
 * Returns the note's full reaction summary.
 */
export const createSalesNoteReaction = (id, noteId, reaction) =>
  client.post(`/api/agent/sales/${id}/notes/${noteId}/reactions`, { reaction });

/** DELETE /api/agent/sales/:id/notes/:noteId/reactions/:reaction — take back own reaction. */
export const deleteSalesNoteReaction = (id, noteId, reaction) =>
  client.delete(`/api/agent/sales/${id}/notes/${noteId}/reactions/${reaction}`);

/** POST /api/agent/sales/:id/notes/:noteId/attachments/upload-url — signed upload URL. */
export const createSalesNoteAttachmentUploadUrl = (id, noteId, data = {}) =>
  client.post(`/api/agent/sales/${id}/notes/${noteId}/attachments/upload-url`, data);

/** POST /api/agent/sales/:id/notes/:noteId/attachments/confirm-upload — persist the row. */
export const createSalesNoteAttachmentConfirm = (id, noteId, data = {}) =>
  client.post(`/api/agent/sales/${id}/notes/${noteId}/attachments/confirm-upload`, data);

/**
 * GET /api/agent/sales/:id/receipts [LIST · paginated]
 * View payment receipts on the sale with linked proof docs.
 * @disposition ADAPT_SCOPE · maps to GET /:id/receipts (service.listReceipts)
 */
export const listSalesReceipts = (id, params = {}) => client.get(`/api/agent/sales/${id}/receipts`, { params });

/**
 * POST /api/agent/sales/:id/receipts
 * Agent uploads a payment receipt (proof) for a Converted/Completed sale; enters PENDING_VALIDATION for HQ finance to approve.
 * @disposition ADAPT_SCOPE · maps to POST /:id/receipts (controller.recordReceipt → service.recordReceipt)
 */
export const createSalesReceiptsById = (id, data = {}) => client.post(`/api/agent/sales/${id}/receipts`, data);

/**
 * DELETE /api/agent/sales/:id/receipts/:receiptId
 * Agent removes a receipt they uploaded that HQ hasn't validated yet.
 * @disposition ADAPT_SCOPE · maps to DELETE /:id/receipts/:receiptId (service.deleteReceipt)
 */
export const deleteSalesReceiptsByIdReceiptId = (id, receiptId, config = {}) => client.delete(`/api/agent/sales/${id}/receipts/${receiptId}`, config);

/**
 * GET /api/agent/sales/:id/commissions [LIST · paginated]
 * 'My commission' panel — only the caller's own commission line(s), never other recipients or HQ/co-broke.
 * @disposition BUILD_NEW · maps to GET /:id/commissions (controller.listCommissions → service.listCommissions)
 */
export const listSalesCommissions = (id, params = {}) => client.get(`/api/agent/sales/${id}/commissions`, { params });

/**
 * GET /api/agent/sales/:id/finance
 * Read-only money view for the caller's own sale: collection rollup, Client A/C held balance
 * (+ held-by-receipt breakdown), invoices (number/total/outstanding/status), refund vouchers,
 * and cash movements. Mirrors the admin Invoices / Client A/C / Refunds tabs; all governance
 * actions stay HQ-only.
 * @disposition BUILD_NEW · maps to GET /:id/finance (controller.finance → service.getSaleFinance)
 */
export const getSalesFinanceById = (id, params = {}) => client.get(`/api/agent/sales/${id}/finance`, { params });

/**
 * GET /api/agent/sales/:id/commission-breakdown — the per-recipient split.
 *
 * `{ scope: 'mine' }` returns ONLY the caller's own lines, totalled over those
 * lines and without Net GCI / balance — what the sale workspace asks for, since
 * an agent must not see another recipient's amount there (and filtering in the
 * component would still put the amounts on the wire). Unscoped it is the full
 * split, which is what the Earnings deal sheet exists to show.
 */
export const getSalesCommissionBreakdownById = (id, params = {}) =>
  client.get(`/api/agent/sales/${id}/commission-breakdown`, { params });

/**
 * GET /api/agent/sales/:id/invoices/:invoiceId/pdf
 * Download the invoice PDF (proxied read-only from SQL Accounting) for an issued invoice on
 * the caller's own sale. Returns a Blob (responseType: 'blob').
 * @disposition BUILD_NEW · maps to GET /:id/invoices/:invoiceId/pdf (controller.invoicePdf)
 */
export const getSalesInvoicePdfById = (id, invoiceId) => client.get(`/api/agent/sales/${id}/invoices/${invoiceId}/pdf`, { responseType: 'blob' });

/**
 * GET /api/agent/sales/:id/payments/:applicationId/pdf
 * Download the OFFICIAL RECEIPT raised when HQ applied a payment to an invoice — the
 * receipt the company issues to the customer, not the bank-in slip the agent uploads.
 * Proxied from SQL Accounting, or served from storage when finance receipted manually.
 * Returns a Blob (responseType: 'blob').
 * @disposition BUILD_NEW · maps to GET /:id/payments/:applicationId/pdf (controller.paymentPdf)
 */
export const getSalesPaymentPdfById = (id, applicationId) => client.get(`/api/agent/sales/${id}/payments/${applicationId}/pdf`, { responseType: 'blob' });

/**
 * GET /api/agent/sales/:id/balance-to
 * The payout instructions on the sale: who finance pays out of the client-account
 * balance, how much, and into which bank account. Returns { entries, total, count }.
 * Every entry carries `can_edit` — HQ's entries are visible but read-only here.
 */
export const listSalesBalanceTo = (id) => client.get(`/api/agent/sales/${id}/balance-to`);

/**
 * GET /api/agent/sales/:id/balance-to/payees
 * The three picker groups — Landlord / Vendor, Tenant / Purchaser, Agent — holding
 * only the parties on THIS sale, each with its saved bank accounts. Under "Agent"
 * the caller is the only option: a co-agent's account number is theirs, not the
 * caller's to see, so a balance to anyone else is raised by HQ.
 */
export const listSalesBalanceToPayees = (id) => client.get(`/api/agent/sales/${id}/balance-to/payees`);

/**
 * POST /api/agent/sales/:id/balance-to — add an instruction.
 * Body: { party_type, party_id, amount, remarks?, and ONE of
 *   bank_account_id  — an account the party already has, or
 *   new_bank_account: { bank_name, account_no, account_holder_name? }
 *     which is created and BOUND to that party for next time }.
 */
export const createSalesBalanceTo = (id, data = {}) => client.post(`/api/agent/sales/${id}/balance-to`, data);

/** PUT /api/agent/sales/:id/balance-to/:entryId — edit an entry the caller raised. */
export const updateSalesBalanceTo = (id, entryId, data = {}) => client.put(`/api/agent/sales/${id}/balance-to/${entryId}`, data);

/** DELETE /api/agent/sales/:id/balance-to/:entryId — remove an entry the caller raised. */
export const deleteSalesBalanceTo = (id, entryId) => client.delete(`/api/agent/sales/${id}/balance-to/${entryId}`);
